import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit, Input } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AdminService } from '../../services/admin.service';
import { ItemMaster } from '../../../shared/models/item.master.interface';
import { ItemType } from '../../../shared/models/item.type.interface';
import { RValue } from '../../../shared/models/r.value.interface';
import { MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { ItemEditComponent } from '../item-edit/item-edit.component';
import { Subscription } from 'rxjs';
import { ItemFilter } from '../../models/item.filter';
import { BulkUpdateComponent } from '../bulk-update/bulk-update.component';
import { BulkUpdateData } from '../models/bulk-update.interface';
import { ItemUpdateTarget_Enum } from '../../enums/item-update-target.enum';
import { BulkUpdateRollbackComponent } from '../bulk-update-rollback/bulk-update-rollback.component';
import { MatTableDataSource } from '@angular/material/table';
import { AbstractBiditComponent } from '../../../shared/controls/abstract.bidit.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FileService } from '../../../shared/services/file.service';
import { ImportItemQtyComponent } from '../import-item-qty/import-item-qty.component';
import { EventService } from '../../../shared/services/event.service';
import { EventType_Enum } from '../../enums/event-type.enum';

export interface ItemDialogData {
  id: any;
  itemTypes: ItemType[];
  rValues: RValue[];
}

@Component({
  selector: 'app-item-list',
  templateUrl: './item-list.component.html',
  styleUrls: ['./item-list.component.scss']
})
export class ItemListComponent extends AbstractBiditComponent implements AfterViewInit, OnInit, OnDestroy {
  dataSource = new MatTableDataSource<ItemMaster>();
  displayedColumns: string[] =
    ['itemName', 'rValue', 'itemType', 'itemCost', 'itemPart', 'itemQtyOnhand', 'action'];

  private dealerSubscription: Subscription;
  private eventSubscription: Subscription;


  itemTypes: ItemType[];
  rValues: RValue[];
  itemFilter: ItemFilter = new ItemFilter();
  showBulkUpdate: boolean = false;
  showBulkUpdateReset: boolean = false;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(private adminService: AdminService, public dialog: MatDialog,
    deviceDetectorService: DeviceDetectorService, private fileService: FileService, private eventService: EventService ) {
    super(deviceDetectorService);
  }


  ngOnInit() {

    this.showBulkUpdateReset = (AdminService._authNavStatusSource.value == "IDI");
    this.dealerSubscription = AdminService.impersonatedDealer$.subscribe(dealerId => {
      this.setupFilters();
    });

    this.eventService.receivedMsg().subscribe({
      next: (data: any) => {
        if (data === EventType_Enum.ItemTypeModification) {
          this.setupFilters();
        }
      }
    })   

    this.dataSource.paginator = this.paginator;

    this.initializeSearchCriteria();

    this.setupFilters();
  }

  ngAfterViewInit(): void {
    // reset the paginator after sorting
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    this.sort.sortChange
      .pipe(
        tap(() => this.filterItems())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.sort.sortChange.unsubscribe();
    this.dealerSubscription.unsubscribe();
  }

  setupFilters() {
    let dealerId = this.adminService.getDealerId();

    this.adminService.getItemTypes(dealerId)
      .subscribe({
        next: (data) => {
          this.itemTypes = data.filter(d => d.itemType_IsActive);
        }
      });

    this.adminService.getrValues()
      .subscribe({
        next: (data) => {
          this.rValues = data;
        }
      });
  }

  initializeSearchCriteria() {
    this.itemFilter.itemTypeId = null;
    this.itemFilter.rValueId = null;
    this.itemFilter.searchText = null;
    this.itemFilter.statusId = 1;
    this.filterItems();
  }

  exportItems() {
    let dealerId = this.adminService.getDealerId();
    this.isRequesting = true;
    this.adminService.exportItemList(this.itemFilter, dealerId)
      .subscribe({
        next: (data) => {
          this.isRequesting = false;
          this.fileService.downloadFile(data, 'ItemList.csv');
        },
        error: (error: string) => this.onError(error)
      });
  }

  filterItems() {
    let dealerId = this.adminService.getDealerId();
    this.isRequesting = true;
    this.adminService.searchItemList(this.itemFilter, dealerId, this.sort.active, this.sort.direction)
      .subscribe({
        next: (data) => {
          this.isRequesting = false;

          this.showBulkUpdate = this.itemFilter.itemTypeId != null || this.itemFilter.rValueId != null || this.itemFilter.searchText != null;
          this.dataSource.data = data;
        }
      });
  }

  showEdit(itemId) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = { id: itemId, itemTypes: this.itemTypes, rValues: this.rValues } as ItemDialogData;
    dialogConfig.width = '1100px';

    const itemDialogRef = this.dialog.open(ItemEditComponent, dialogConfig);

    itemDialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.filterItems();
    });
  }

  bulkUpdate() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { items: this.dataSource.data, itemUpdateTarget: ItemUpdateTarget_Enum.ItemMaster, itemFilter: this.itemFilter } as BulkUpdateData;
    dialogConfig.width = '700px';

    const bulkUpdateDialogRef = this.dialog.open(BulkUpdateComponent, dialogConfig);

    bulkUpdateDialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.filterItems();
    });
  }

  bulkUpdateReset() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '1100px';

    const bulkUpdateDialogRef = this.dialog.open(BulkUpdateRollbackComponent, dialogConfig);

    bulkUpdateDialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.filterItems();
    });
  }


  toggleStatus(page: string, id) {
    let dealerId = this.adminService.getDealerId();

    this.adminService.toggleStatus(page, id, dealerId)
      .subscribe(result => {
        console.log(result);
        if (result.id > 0) {
          let items = this.dataSource.data.filter(al => al.item_ID == id);
          if (items.length == 0) return;
          items[0].item_isActive = result.isActive;

          let activeStatusId = page + "_status_active" + id;
          let inActiveStatusId = page + "_status_inactive" + id;

          if (result.isActive) {
            document.getElementById(activeStatusId).className = "text-success";
            document.getElementById(inActiveStatusId).className = "";
          }
          else {
            document.getElementById(activeStatusId).className = "";
            document.getElementById(inActiveStatusId).className = "text-danger";
          }
        }
      });
  }

  itemQtyImport() {

      let dialogData = {

      } as ImportItemQtyComponent;

      const dialogRef = this.dialog.open(ImportItemQtyComponent, {
        width: '75%',
        data: dialogData
      });

    dialogRef.afterClosed().subscribe(result => {
      this.filterItems();
        console.log('The dialog was closed');        
      });
  }
}
